// Agent playbooks — builder authoring (/api/playbooks) and the run-time
// administration adapter (/api/playbook-runtime) the shared Agents panel uses.
import api from './client'
import type {
  AgentsRuntime, Assignment, Cycle, MonitorSummary, Persona, PlaybookRun, PlaybookSummary,
} from '@twasta/page-runtime/shell/playbooks'

/** The server's own sentence, not axios's "Request failed with status code 400". */
export function errorText(err: unknown, fallback = 'Something went wrong.'): string {
  const anyErr = err as { response?: { data?: { detail?: unknown } }; message?: string }
  const detail = anyErr?.response?.data?.detail
  if (typeof detail === 'string' && detail) return detail
  if (Array.isArray(detail) && detail.length) {
    return detail.map((d) => (d && typeof d === 'object' && 'msg' in d ? String((d as { msg: unknown }).msg) : String(d))).join('; ')
  }
  return anyErr?.message || fallback
}

async function call<T>(p: Promise<{ data: T }>): Promise<T> {
  try {
    return (await p).data
  } catch (err) {
    throw new Error(errorText(err))
  }
}

// ── authoring ────────────────────────────────────────────────────────────

export type StepKind =
  | 'query' | 'reason' | 'write' | 'process' | 'report' | 'human_input'
  | 'external_input' | 'approval' | 'integration_call' | 'wait' | 'for_each' | 'tool'

/** A playbook's own Python tool (code at src/playbook_models/<playbook>/tool__<name>.py). */
export interface CustomTool {
  name: string
  description?: string
  input_schema?: Record<string, unknown>
  uses?: { obj_name: string; actions?: string[] }[]
  business_areas?: string[]
  writes?: boolean
}

export interface PlaybookStep {
  id: string
  kind: StepKind
  title?: string
  obj_name?: string
  op?: 'add' | 'edit' | 'delete' | 'action'
  action_name?: string
  prompt?: string
  output?: string[]
  values?: Record<string, unknown>
  key?: Record<string, unknown>
  filters?: { column: string; op?: string; value?: unknown }[]
  over?: string
  body?: PlaybookStep[]
  assignee_role?: string
  recipient?: string
  mode?: string
  event?: string
  api?: string
  duration?: string
  timeout?: string
  [key: string]: unknown
}

export interface ProposalItem {
  id: string
  kind: 'role' | 'grant' | 'workflow' | 'integration_event' | 'integration_api'
    | 'ioflow_seed' | 'ioflow_flow' | 'schedule' | 'playbook_save'
  title: string
  why?: string
  payload: Record<string, any>
  depends_on: string[]
  status: 'proposed' | 'accepted' | 'rejected' | 'applied' | 'handoff' | 'pending_admin' | 'verified' | 'failed'
  apply_result?: Record<string, any> | null
}

export interface Playbook {
  name: string
  title?: string
  goal: string
  instructions?: string
  version?: number
  inputs?: { name: string; type?: string; required?: boolean; default?: unknown }[]
  steps: PlaybookStep[]
  required_profile?: { role: string; description?: string; grants: { obj_name: string; actions: string[] }[] }
  tools?: { allow?: string[] }
  custom_tools?: CustomTool[]
  limits?: Record<string, number>
  proposals?: { items: ProposalItem[] }
  [key: string]: unknown
}

export interface OpenQuestion { question: string; options?: string[]; recommended?: string }

/** A playbook with its derived profile/checklist and what still needs a decision. */
export interface DeriveResult {
  playbook: Playbook
  open_questions: OpenQuestion[]
  problems: string[]
  analysis: { external_systems: Record<string, unknown>[]; run_cycle: Cycle | null }
}

export interface VerifyResult { ok: boolean; checks: { check: string; ok: boolean; detail?: string }[] }

export const playbooksApi = {
  list: (project: string) =>
    call<{ playbooks: { name: string; title: string; goal: string; version: number; steps: number; role: string }[] }>(
      api.get('/playbooks', { params: { project } })),
  load: (project: string, name: string) =>
    call<Playbook>(api.get('/playbooks/item', { params: { project, name } })),
  save: (project: string, playbook: Playbook) =>
    call<Playbook>(api.post('/playbooks/save', { project, playbook })),
  validate: (project: string, playbook: Playbook) =>
    call<{ ok: boolean; problems: string[] }>(api.post('/playbooks/validate', { project, playbook })),
  remove: (project: string, name: string) =>
    call<{ deleted: boolean }>(api.delete('/playbooks/item', { params: { project, name } })),
  grounding: (project: string) =>
    call<Record<string, any>>(api.get('/playbooks/grounding', { params: { project } })),
  repropose: (project: string, playbook: Playbook, analysis?: DeriveResult['analysis']) =>
    call<DeriveResult>(api.post('/playbooks/repropose', { project, playbook, analysis: analysis || {} })),
  apply: (project: string, playbook: Playbook, accept?: string[]) =>
    call<{ playbook: Playbook; verify: VerifyResult }>(api.post('/playbooks/apply', { project, playbook, accept }, { timeout: 300000 })),
  verify: (project: string, name: string) =>
    call<VerifyResult>(api.get('/playbooks/verify', { params: { project, name } })),
}

// ── run time (System administration → Agents) ────────────────────────────

export function makeAgentsRuntime(
  ownerId: string, project: string,
  opts: { listRoles(): Promise<string[]>; notify?(text: string, tone?: 'success' | 'error' | 'info'): void },
): AgentsRuntime {
  const base = `/playbook-runtime/${encodeURIComponent(ownerId)}/${encodeURIComponent(project)}`
  return {
    listPlaybooks: async () => (await call<{ playbooks: PlaybookSummary[] }>(api.get(`${base}/playbooks`))).playbooks,
    listRoles: opts.listRoles,
    listPersonas: async () => (await call<{ personas: Persona[] }>(api.get(`${base}/personas`))).personas,
    createPersona: (body) => call<Persona>(api.post(`${base}/personas`, body)),
    updatePersona: (id, body) => call<Persona>(api.patch(`${base}/personas/${encodeURIComponent(id)}`, body)),
    deletePersona: async (id) => { await call(api.delete(`${base}/personas/${encodeURIComponent(id)}`)) },
    assign: (id, body) => call<Assignment>(api.post(`${base}/personas/${encodeURIComponent(id)}/assignments`, body)),
    updateAssignment: (id, body) => call<Assignment>(api.patch(`${base}/assignments/${encodeURIComponent(id)}`, body)),
    unassign: async (id) => { await call(api.delete(`${base}/assignments/${encodeURIComponent(id)}`)) },
    runNow: (id) => call<PlaybookRun>(api.post(`${base}/assignments/${encodeURIComponent(id)}/run`, { inputs: {} })),
    monitor: () => call<MonitorSummary>(api.get(`${base}/monitor`)),
    listRuns: async (params) => (await call<{ runs: PlaybookRun[] }>(api.get(`${base}/runs`, { params }))).runs,
    getRun: (id) => call<PlaybookRun>(api.get(`${base}/runs/${encodeURIComponent(id)}`)),
    transcript: async (runId, stepId) => (await call<{ messages: Record<string, unknown>[] }>(
      api.get(`${base}/runs/${encodeURIComponent(runId)}/transcript/${encodeURIComponent(stepId)}`))).messages,
    control: (id, op, uncertain) => call<{ state: string }>(
      api.post(`${base}/runs/${encodeURIComponent(id)}/${op}`, { uncertain: uncertain || '' })),
    notify: opts.notify,
  }
}

/** Answer a playbook's Work List task (any assignee). */
export function answerPlaybookTask(ownerId: string, project: string, sourceRef: string,
  answer: Record<string, unknown>) {
  return call<{ ok: boolean }>(api.post(
    `/playbook-runtime/${encodeURIComponent(ownerId)}/${encodeURIComponent(project)}/tasks/answer`,
    { source_ref: sourceRef, answer }))
}

/** The public one-time link (no session). */
export const playbookLinkApi = {
  view: (ownerId: string, project: string, token: string) =>
    call<{ title: string; mode: string; form: { fields?: any[] }; context: Record<string, unknown>; expires_at: string }>(
      api.get(`/playbook-input/${encodeURIComponent(ownerId)}/${encodeURIComponent(project)}/${encodeURIComponent(token)}`)),
  answer: (ownerId: string, project: string, token: string, body: Record<string, unknown>) =>
    call<{ ok: boolean; message: string }>(
      api.post(`/playbook-input/${encodeURIComponent(ownerId)}/${encodeURIComponent(project)}/${encodeURIComponent(token)}`, body)),
}
